import type React from "react"

import type {
  BackendBillingPlanName,
  EntitlementType,
  SubscriptionStatus,
} from "@/billing/billing"
import type { LrpStatus, RegionName } from "@/infra"

export type Uuid = string
export type Fingerprint = string

export type NodeId = string

export type Visibility = "public" | "private"

export interface IrohError {
  code: string // a fixed string that never changes (useful for switch statements)
  error: string // a human-readable error message, may include contextual details like email address
}

export enum UserType {
  individual = "individual",
  team = "team",
}

export interface User {
  id: Uuid
  gravatar_hash?: string
  name?: string
  username: string
  email: string
  pending_email?: string | null
  user_type: UserType.individual
  email_is_verified: boolean
  email_verification_expires_at: number
  discord_user_id?: number
  discord_username?: string
  is_staff?: boolean
  trial_used: boolean
}

export type Project = {
  id: Uuid
  username: string
  name: string
  owner_id: string
  created_at: number
  updated_at: number
  /// If `None`, means trial has expired.
  billing_plan?: BackendBillingPlanName
  subscription_status: SubscriptionStatus
  billing_name: string
  billing_email: string
  gravatar_hash?: string
  entitlements: EntitlementType[]
  visibility?: Visibility
  description?: string
  /** Maximum number of relays for this plan. `null` means unlimited. */
  max_relays?: number | null
  /** Hard cap on concurrent authenticated endpoints. */
  max_authenticated_endpoints?: number
  /** Maximum number of collaborators (project members). */
  max_collaborators?: number
  /** Maximum number of diagnostic reports kept per project. */
  max_diagnostic_reports?: number
  /** Whether this project has active paid resources (relays/DNS). */
  has_active_paid_resources?: boolean
  /** Monthly spend cap in cents. `null` means no cap. */
  spend_cap_cents?: number | null
  /** Whether the spend cap has been reached this billing period. */
  spend_cap_reached?: boolean
  /** Pending plan migration initiated by staff, if any. */
  pending_plan_migration?: {
    id: string
    target_plan: BackendBillingPlanName
    created_at: string
  }
}

export interface UserUpdates {
  username?: string
  name?: string
}

export interface BlobInfo {
  name?: string
  path: string
  format: BlobFormat
  hash: string
  size: number
}

export enum BlobFormat {
  Raw = "raw",
  HashSeq = "hash_seq",
}

export interface Collection {
  doc_id: string
  ticket: string
}

export interface GossipInfo {
  label: string
  topic: string
  bootstrap: string[]
}

export interface IrohProviderEvent {
  timestamp: number
  type: string
  event: string
}

export enum ContentProvidePolicy {
  Disabled = "Disabled",
  Public = "Public",
  Private = "Private",
}

export enum ProjectRole {
  Admin = "admin",
  Member = "member",
}

export const teamRoles = [
  {
    role: ProjectRole.Admin,
    description: "Has full administrative access to the entire project",
  },
  {
    role: ProjectRole.Member,
    description:
      "Access to all project resources. Can see every member and their roles, but cannot edit collaborators",
  },
]

export interface TeamIdAndMember {
  teamId: string
  member: ProjectMember
}

export interface UserOrTeam {
  user_type: UserType
  id: Uuid
  username: string
  entitlements: EntitlementType[]
}

export type UserOrTeamRes = {
  user_type: UserType
  id: Uuid
  username: string
  billing_plan?: BackendBillingPlanName
  entitlements?: EntitlementType[]
  role?: ProjectRole
}

export interface UserProject {
  id: Uuid
  username: string
  name: string
  billing_plan?: BackendBillingPlanName
  subscription_status: SubscriptionStatus
  entitlements: EntitlementType[]
  role?: ProjectRole
}

export interface ProjectMember {
  username: string
  user_id: Uuid
  role: ProjectRole
  gravatar_hash?: string
}

export interface ProjectInvite {
  /// The actual invite code.
  code: Uuid
  /// Username, if invited user already exists
  username?: string
  /// The user id that was created.
  user_id?: Uuid
  email?: string
  /// The user that created this invite.
  created_by: Uuid
  created_at: Date
  /// The team this invite is for.
  project_id?: Uuid
  role: ProjectRole
  // name of the project in :owner/:project_name form
  name?: string
  used_at?: Date
}

// invite When it's part of a listing response
export interface ProjectInviteItem {
  /// The actual invite code.
  code: Uuid
  /// Username, if invited user already exists
  username?: string
  /// The user id that was created.
  user_id?: Uuid
  email?: string
  /// The user that created this invite.
  created_by: Uuid
  created_at: Date
  role: ProjectRole
  name?: string
  used_at?: Date
}

export enum SignupRedirects {
  CreateTeamStartup = "create-team-startup",
}

export interface StatsResponse {
  library: LibraryStat[]
  trafficSummary: TrafficStatPrior
  traffic: TrafficStat[]
  requests: RequestWithName[]
}

export interface Pagination {
  offset: number
  limit: number
}

export type ProjectIdParam = {
  project_id: Uuid
}

export type ApiKeyCreateResponse = {
  key_id: string
  name: string
  created_at: number
  secret: string
}

export enum StatDuration {
  hour = 60 * 60,
  twentyFourHours = 60 * 60 * 24,
  sevenDays = 60 * 60 * 24 * 7,
}

export interface EgressStats {
  summary: EgressStatsSummary
  daily: EgressStatsAt[]
}

export interface EgressStatsSummary {
  egress_bytes: number
  egress_blobs: number
  requests: number
}

export interface EgressStatsAt {
  date: string
  egress_bytes: number
  egress_blobs: number
  requests: number
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface ChangePasswordRequest {
  old_password: string
  new_password: string
}

export interface VerifyEmailResponse {
  status: string
}

export interface VerifyEmailRequest {
  token: string
}

export interface IrohDeployInfo {
  instance_id: Uuid
  deploy_id: Uuid
  metrics_label: string
  url: string
  region: RegionName
  lat: number
  lon: number
  status: LrpStatus
  build_id?: Uuid
  build_tag?: string
}

export interface NetworkStat {
  startTime: number
  interval: number
  connections: number
  uniqueNodes: number
}

export interface PromApiResponseStat {
  value: {
    timestamp: number
    value: number
  }
}

export interface User {
  username: string
  email: string
}

export interface LibraryObject {
  identifier: string
  hash: string
  size: number
  created: number
}

export interface LibraryStat {
  startTime: Date
  contentCount: number
  contentSize: number
}

export interface TrafficStatPrior {
  startTime: number
  countIroh: number
  countIrohPrior: number
  countHttps: number
  countHttpsPrior: number
  sizeIroh: number
  sizeIrohPrior: number
  sizeHttps: number
  sizeHttpsPrior: number
}

export interface TrafficStat {
  startTime: number
  countIroh: number
  countHttps: number
  sizeIroh: number
  sizeHttps: number
}

export interface Content {
  identifier: string
  hash: string
  size: number
  created: number
  headers: Record<string, string>
}

export interface Device {
  nodeId: string
  timestamp: string
  deviceCategory: "Desktop" | "Mobile" | "Tablet"
  completeObjects: LibraryObject[]
  incompleteObjects: LibraryObject[]
}

export interface Object {
  id: string
  name: string
  hash: string
  created: Date
}

export enum FetchSource {
  Iroh = "Iroh",
  Https = "Https",
}

export enum FetchResult {
  Unknown = "Unknown",
  Success = "Success",
  Failure = "Failure",
  Partial = "Partial",
}

export interface RequestWithName {
  requestor: string
  source: FetchSource
  start: Date
  result: FetchResult
  name: string
  hash: string
  duration: number
  transferred: number
}

export enum ProtocolType {
  irohServicesRpc = "iroh-services-rpc",
  blobs = "blobs",
  gossip = "gossip",
}

export type ProtocolDetails = {
  protocol: ProtocolType
  icon: React.FC
  title: string
  tagline: string
  slug: string
  repository: string
  rustdoc: string
  documentation: string
  version: string
  featured?: number
  comingSoon?: boolean
}

export type Protocol = {
  protocol: ProtocolType
  project_id: Uuid
  created_at: number
  updated_at: number
  config: Record<string, any>
}

export type SshKeyInfo = {
  id: Uuid
  owner_id: Uuid
  fingerprint: Fingerprint
  name: string
  comment: string
  is_revoked: boolean
  created_at: number
  last_access: number
}

export type ApiKeyInfo = {
  id: Uuid
  project_id: Uuid
  name: string
  secret: string
}

export type IrohNode = {
  node_id: string
  auth_token: string
  auth_token_expires_at: string | null
  last_access: string
  name: string
  project_id: Uuid
}

/** A row in the endpoints list. Carries display-only fields (e.g.
 *  `latest_iroh_version`) that aren't part of a node's core backend identity
 *  and shouldn't leak into {@link IrohNode}. Mirrors the backend
 *  `IrohNodeListRow`; `last_access`/`auth_token` are nullable here because the
 *  list query left-joins data a freshly created node may not have yet. */
export type IrohNodeListRow = {
  node_id: string
  name: string
  project_id: Uuid
  auth_token: string | null
  auth_token_expires_at: string | null
  last_access: string | null
  /** Iroh version from the endpoint's most recent net-diagnostics report,
   *  or `null` if no report exists. Folded into the list payload to avoid a
   *  per-row query. */
  latest_iroh_version: string | null
}

export type TransportAddr = { Relay: string } | { Ip: string }

export type EndpointAddr = {
  id: string
  addrs: TransportAddr[]
}

export type Endpoint = {
  node_id: string
  name: string
  project_id: Uuid
  is_enabled: boolean
  cloud_node_addr?: EndpointAddr
}

export const endpointDefault: Endpoint = {
  node_id: "$NODE_ID",
  name: "Endpoint",
  project_id: "$OWNER_ID",
  is_enabled: true,
}

export type PingMessage = {
  timestamp: Date
  node_id: string
}

export type MetricNodeInfo = {
  node_id: string
  name: string
  last_access?: string
}
